import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckCircleIcon, StarIcon, UsersIcon, TrendingUpIcon } from 'lucide-react';
const trustBadges = [{
  icon: UsersIcon,
  text: '110+ Unternehmer'
}, {
  icon: StarIcon,
  text: 'Close CRM Partner'
}, {
  icon: TrendingUpIcon,
  text: '3.2x ROI Durchschnitt'
}];
const clientLogos = ['Alessandro Principe', 'Ennea Scholars', 'automadeit', 'Ecom Wave', 'Thorsten Wittmann'];
// Helper function to get current month name in German
const getCurrentMonthName = () => {
  const months = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  return months[new Date().getMonth()];
};
// Helper function to get available spots based on day of month
const getAvailableSpots = () => {
  const dayOfMonth = new Date().getDate();
  if (dayOfMonth <= 10) return 3;
  if (dayOfMonth <= 20) return 2;
  return 1;
};
export function Hero() {
  const currentMonth = getCurrentMonthName();
  const availableSpots = getAvailableSpots();
  return <section className="relative min-h-screen bg-[#0a0a0a] overflow-hidden" data-id="element-106" data-mp="3o59bpicfn4e0">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0a0a0a] via-[#0a0a0a] to-[#0c1929] pointer-events-none" data-id="element-107" data-mp="3e9fqlakne2km" />
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-[#0ea5e9]/5 blur-[120px] pointer-events-none" data-id="element-108" data-mp="30hrgtdxvfq6t" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 lg:pt-32 lg:pb-24" data-id="element-109" data-mp="359t19n570n7x">
        {/* Badge */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="flex justify-center mb-8" data-id="element-110" data-mp="2qlepgmpstof8">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0ea5e9]/10 border border-[#0ea5e9]/20 text-[#0ea5e9] text-sm font-medium" data-id="element-111" data-mp="2lk8v6kghpt3l">
            <span className="w-2 h-2 bg-[#0ea5e9] rounded-full animate-pulse" data-id="element-112" data-mp="nat6j94y4gjn" />
            Nur noch {availableSpots}{' '}
            {availableSpots === 1 ? 'Platz' : 'Plätze'} im {currentMonth}{' '}
            verfügbar
          </span>
        </motion.div>

        {/* Main headline */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="text-center max-w-5xl mx-auto" data-id="element-113" data-mp="1b0nizplcs33d">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight tracking-tight" data-id="element-114" data-mp="x030kujco9ae">
            Die <span className="text-[#0ea5e9]" data-id="element-115" data-mp="3lu9wgyz13ua6">Close CRM Agentur</span> für{' '}
            <span className="relative" data-id="element-116" data-mp="3hqam6y4cdrp9">
              skalierbaren Vertrieb
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none" data-id="element-117" data-mp="1x9ukuaytndnf">
                <path d="M2 10C50 4 150 2 298 10" stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" data-id="element-118" data-mp="218jwnz47on4y" />
              </svg>
            </span>
          </h1>

          <p className="mt-8 text-lg sm:text-xl lg:text-2xl text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-119" data-mp="2g0luzeqmv82v">
            Wir implementieren Close CRM so, dass Ihr Vertriebsteam{' '}
            <span className="text-white font-medium" data-id="element-120" data-mp="2ozz4g7erbwnl">47% mehr Abschlüsse</span>{' '}
            erzielt – ohne zusätzliche Mitarbeiter, ohne Chaos, ohne
            Datenverlust.
          </p>
        </motion.div>

        {/* Trust badges */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} className="flex flex-wrap justify-center gap-6 mt-10" data-id="element-121" data-mp="uo0u224pouq4">
          {trustBadges.map((badge, index) => <div key={index} className="flex items-center gap-2 text-gray-400" data-id="element-122" data-mp="e88w40yame3y" data-mpi={index}>
              <badge.icon className="w-5 h-5 text-[#0ea5e9]" data-id="element-123" data-mp="9a7elkgimmul" />
              <span className="text-sm font-medium" data-id="element-124" data-mp="98mezgfcgdpv">{badge.text}</span>
            </div>)}
        </motion.div>

        {/* CTAs */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="flex flex-col sm:flex-row justify-center gap-4 mt-12" data-id="element-125" data-mp="3cs8r46wxglr3">
          <a href="/Kostenloses-Strategiegespraech" className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#0ea5e9] text-white font-semibold rounded-lg hover:bg-[#0284c7] transition-all duration-200 shadow-lg shadow-[#0ea5e9]/25" data-id="element-126" data-mp="2yxok6o64nopw">
            Kostenloses Strategiegespräch
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-127" data-mp="zkzp1ezhjpj1" />
          </a>
          <a href="#case-studies" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/5 text-white font-semibold rounded-lg border border-white/10 hover:bg-white/10 transition-all duration-200" data-id="element-128" data-mp="14isu24p1ydhq">
            Erfolgsgeschichten ansehen
          </a>
        </motion.div>

        {/* Risk reversal */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.6
      }} className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500" data-id="element-129" data-mp="1jata7a7p36ea">
          <span className="flex items-center gap-2" data-id="element-130" data-mp="2ehaq1opd8k6y">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-131" data-mp="2eh0zo00esb0q" />
            30 Tage Geld-zurück-Garantie
          </span>
          <span className="flex items-center gap-2" data-id="element-132" data-mp="29ktptw4w6fdi">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-133" data-mp="24qpkfhb9yfej" />
            Kostenlose CRM-Analyse
          </span>
          <span className="flex items-center gap-2" data-id="element-134" data-mp="1zs4p9wavqlwy">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" data-id="element-135" data-mp="1vrlxu3cwfhva" />
            Keine versteckten Kosten
          </span>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.7,
        delay: 0.7
      }} className="grid grid-cols-2 lg:grid-cols-4 gap-8 mt-20 pt-12 border-t border-white/10" data-id="element-136" data-mp="1qu2h8wsqat06">
          {[{
          value: '110+',
          label: 'Unternehmer betreut'
        }, {
          value: '3.2x',
          label: 'Durchschnittlicher ROI'
        }, {
          value: '89%',
          label: 'Zeitersparnis im Vertrieb'
        }, {
          value: '47%',
          label: 'Mehr Abschlüsse'
        }].map((stat, index) => <div key={index} className="text-center" data-id="element-137" data-mp="30q959qgwtb2m" data-mpi={index}>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white" data-id="element-138" data-mp="30rshn5zg31z4">
                {stat.value}
              </div>
              <div className="mt-2 text-sm text-gray-500" data-id="element-139" data-mp="39qxgyeityzcc">{stat.label}</div>
            </div>)}
        </motion.div>

        {/* Client logos */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.9
      }} className="mt-20" data-id="element-140" data-mp="3erbq5zw64fby">
          <p className="text-center text-sm text-gray-600 mb-8" data-id="element-141" data-mp="1f2skqjio5m7f">
            VERTRAUT VON FÜHRENDEN UNTERNEHMEN
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-16 mb-16" data-id="element-142" data-mp="1b3a2xwm59r8t">
            {clientLogos.map((logo, index) => <span key={index} className="text-gray-600 font-semibold text-lg" data-id="element-143" data-mp="1k2naukume7rf" data-mpi={index}>
                {logo}
              </span>)}
          </div>

          {/* Close CRM Partner Badges */}
          <div className="mt-12 pt-12 border-t border-white/10" data-id="element-144" data-mp="1tsgn7o2l6qwq">
            <p className="text-center text-sm text-gray-600 mb-8" data-id="element-145" data-mp="1trykq9pbzggd">
              OFFIZIELLER CLOSE CRM PARTNER
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 lg:gap-12" data-id="element-146" data-mp="27pvfr1o4zesx">
              <motion.img initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 1.0
            }} src="https://cdn.magicpatterns.com/uploads/3FWPoSStYkTeUgaax2MNNb/Close_PartnerBadge_Top10_24.png" alt="Close CRM Top 10 Partner 2024" className="w-32 h-32 object-contain" loading="lazy" width="128" height="128" data-id="element-147" data-mp="22r247esetxve" />
              <motion.img initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 1.1
            }} src="https://cdn.magicpatterns.com/uploads/pQdCjF5fWpKETcGWQmuMHZ/Close_PartnerBadge_Since22.png" alt="Close CRM Partner Since 2022" className="h-16 object-contain" loading="lazy" width="auto" height="64" data-id="element-148" data-mp="21aegqj4dhreb" />
              <motion.img initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 1.2
            }} src="https://cdn.magicpatterns.com/uploads/ae4DuFRYPVbUpuQ7e3dsY4/Close_PartnerBadge_Tier3.png" alt="Close CRM Solutions Partner Tier 3" className="w-32 h-32 object-contain" loading="lazy" width="128" height="128" data-id="element-149" data-mp="fv3q97dwrfnu" />
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}